import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMemo } from 'react';
import type { JourneyCanvas } from '@/api/marketing.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import {
  canvasFromSteps,
  defaultStepLabel,
  JOURNEY_ACTION_KINDS,
  JOURNEY_NODE_TYPES,
  newStepKey,
  orderNodesLinearly,
  stepsFromCanvas,
  validateCanvas,
  type JourneyNodeType,
  type JourneyStep,
} from '@/modules/marketing/utils/journeyCanvas';
import { cn } from '@/utils/cn';

export { parseCanvasJson } from '@/modules/marketing/utils/journeyCanvas';

type Props = {
  canvas: JourneyCanvas;
  onChange: (canvas: JourneyCanvas) => void;
  onSave: () => void;
  saving?: boolean;
  readOnly?: boolean;
  triggerLabel?: string;
};

function nodeStyle(type: string) {
  switch (type) {
    case 'GOAL':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
    case 'DELAY':
      return 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
    case 'TRIGGER':
      return 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300';
    default:
      return 'border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300';
  }
}

export function JourneyCanvasEditor({
  canvas,
  onChange,
  onSave,
  saving,
  readOnly,
  triggerLabel,
}: Props) {
  const steps = useMemo(() => stepsFromCanvas(canvas), [canvas]);
  const validationError = useMemo(() => validateCanvas(canvas), [canvas]);
  const previewNodes = useMemo(() => orderNodesLinearly(canvas), [canvas]);

  const applySteps = (nextSteps: JourneyStep[]) => {
    onChange(canvasFromSteps(nextSteps));
  };

  const updateStep = (index: number, patch: Partial<JourneyStep>) => {
    const next = steps.map((s, i) => (i === index ? { ...s, ...patch } : s));
    applySteps(next);
  };

  const removeStep = (index: number) => {
    if (index === 0 || steps.length <= 1) return;
    applySteps(steps.filter((_, i) => i !== index));
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    if (index === 0) return;
    const target = index + direction;
    if (target <= 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    applySteps(next);
  };

  const addStep = (type: JourneyNodeType) => {
    const key = newStepKey(type, steps);
    applySteps([
      ...steps,
      {
        key,
        type,
        label: defaultStepLabel(type, triggerLabel),
        delayAmount: type === 'DELAY' ? 2 : undefined,
        delayUnit: type === 'DELAY' ? 'days' : undefined,
        actionKind: type === 'ACTION' ? 'EMAIL' : undefined,
      },
    ]);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Build the journey as ordered steps. Connections are created automatically from top to bottom.
      </p>

      <div className="space-y-3">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-900/40"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Step {index + 1}
              </span>
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={index <= 1}
                    onClick={() => moveStep(index, -1)}
                    aria-label="Move up"
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={index === steps.length - 1}
                    onClick={() => moveStep(index, 1)}
                    aria-label="Move down"
                  >
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={index === 0 || steps.length <= 1}
                    onClick={() => removeStep(index)}
                    aria-label="Remove step"
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </Button>
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Step type"
                value={step.type}
                disabled={readOnly || (index === 0 && step.type === 'TRIGGER')}
                options={JOURNEY_NODE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                onChange={(v) => {
                  const type = v as JourneyNodeType;
                  updateStep(index, {
                    type,
                    label: defaultStepLabel(type, triggerLabel),
                    delayAmount: type === 'DELAY' ? 2 : undefined,
                    delayUnit: type === 'DELAY' ? 'days' : undefined,
                    actionKind: type === 'ACTION' ? 'EMAIL' : undefined,
                  });
                }}
              />

              {step.type === 'ACTION' ? (
                <Select
                  label="Action"
                  value={step.actionKind ?? 'EMAIL'}
                  disabled={readOnly}
                  options={JOURNEY_ACTION_KINDS.map((k) => ({ value: k.value, label: k.label }))}
                  onChange={(v) =>
                    updateStep(index, {
                      actionKind: v as JourneyStep['actionKind'],
                      label: JOURNEY_ACTION_KINDS.find((k) => k.value === v)?.label ?? step.label,
                    })
                  }
                />
              ) : null}

              {step.type === 'DELAY' ? (
                <>
                  <Input
                    label="Wait amount"
                    type="number"
                    min={1}
                    disabled={readOnly}
                    value={step.delayAmount ?? 2}
                    onChange={(e) =>
                      updateStep(index, { delayAmount: Math.max(1, Number(e.target.value) || 1) })
                    }
                  />
                  <Select
                    label="Unit"
                    value={step.delayUnit ?? 'days'}
                    disabled={readOnly}
                    options={[
                      { value: 'hours', label: 'Hours' },
                      { value: 'days', label: 'Days' },
                    ]}
                    onChange={(v) => updateStep(index, { delayUnit: v as 'hours' | 'days' })}
                  />
                </>
              ) : null}

              <div className={step.type === 'DELAY' ? 'sm:col-span-2' : undefined}>
                <Input
                  label="Display label"
                  disabled={readOnly}
                  value={step.label}
                  onChange={(e) => updateStep(index, { label: e.target.value })}
                  placeholder={defaultStepLabel(step.type, triggerLabel)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => addStep('ACTION')}>
            + Action
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => addStep('DELAY')}>
            + Delay
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => addStep('GOAL')}>
            + Goal
          </Button>
        </div>
      )}

      <div className="overflow-x-auto pb-2">
        <p className="mb-2 text-xs text-slate-500">Flow preview</p>
        <div className="flex min-w-max items-center gap-2">
          {previewNodes.map((node, i) => (
            <div key={node.key} className="flex items-center gap-2">
              <div
                className={cn(
                  'min-w-[88px] rounded-lg border-2 px-3 py-2 text-center text-xs font-medium',
                  nodeStyle(node.type),
                )}
              >
                <div className="text-[10px] text-slate-400">{node.type}</div>
                {node.label}
              </div>
              {i < previewNodes.length - 1 && (
                <span className="text-slate-300 dark:text-slate-600">→</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {validationError && <p className="text-xs text-red-600">{validationError}</p>}

      {!readOnly && (
        <Button loading={saving} onClick={onSave} disabled={Boolean(validationError)}>
          Save journey flow
        </Button>
      )}
    </div>
  );
}
